import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ResizableBox } from 'react-resizable';
import { useLocation } from 'react-router-dom';
import { useGlobalChat } from '../../contexts/GlobalChatContext';
import { useEditorV2 } from '../../contexts/EditorV2Context';
import ChatBot from '../chatbot/ChatBot';
import ChatBtnIcon from '../chatbot/ChatBtnIcon';
import { useAuth } from '../../auth/workosAuth';

const GlobalChatbot = ({ 
  // General props
  className,
  ...otherProps 
}) => {
  const { 
    isChatbotVisible, 
    chatbotWidth, 
    showChatbot, 
    hideChatbot, 
    showError,
    chatBtnRef,
    chatSidebarRef,
    sqlPropsRef,
    setChatbotWidth
  } = useGlobalChat();
  
  const location = useLocation();
  const isOnSQLEditor = location.pathname === '/sql/editor';
  const isOnCodePage = location.pathname === '/code';
  const isOnCampaignWizard = location.pathname === '/sms/new';

  // Track editor version in state to react to changes
  const [sqlEditorVersion, setSqlEditorVersion] = useState(
    localStorage.getItem('sqlEditorVersion') || 'v2'
  );

  // Listen for storage changes (when user toggles v1/v2 in navbar)
  useEffect(() => {
    const handleStorageChange = () => {
      setSqlEditorVersion(localStorage.getItem('sqlEditorVersion') || 'v2');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Check if v2 is selected - hide floating button when it is (button is in navbar instead)
  const isV2Selected = isOnSQLEditor && sqlEditorVersion === 'v2';
  
  // State to force re-reads of sqlPropsRef
  const [, forceUpdate] = useState({});
  
  // Force re-reading of SQL props when chatbot is visible
  useEffect(() => {
    if (isChatbotVisible && isOnSQLEditor) {
      const interval = setInterval(() => {
        forceUpdate({});
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isChatbotVisible, isOnSQLEditor]);
  
  // Only get SQL-specific props if on SQL Editor page
  const sqlProps = isOnSQLEditor ? (sqlPropsRef.current || {}) : {};
  
  const { user } = useAuth();
  const { activeVSCode } = useEditorV2();
  const isDarkMode = !!activeVSCode;
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate button position - bottom right corner with some margin, avoid footer
  const buttonTop = windowHeight - 140;
  const isSmallScreen = window.innerWidth < 768;

  const chatbotElement = (
    <>
      {/* Chat Button - visible when chatbot sidebar is not open AND not in v2 mode AND not on campaign wizard */}
      {!isChatbotVisible && !isV2Selected && !isOnCampaignWizard && !isOnCodePage && (
        <ChatBtnIcon
          ref={chatBtnRef}
          top={buttonTop}
          openSidebar={() => showChatbot()}
          setError={showError}
        />
      )}

      {/* Chat Sidebar - only visible when opened */}
      {isChatbotVisible && (
        <ResizableBox
          className={`chat-sidebar sidebar-modal-wrapper ${isDarkMode ? 'dark-theme' : ''} ${className || ''}`}
          width={chatbotWidth}
          height={windowHeight}
          minConstraints={[isSmallScreen ? window.innerWidth - 300 : 160, windowHeight]}
          maxConstraints={[window.innerWidth - 300, windowHeight]}
          resizeHandles={['w']}
          onResize={(event, { size }) => setChatbotWidth(size.width)}
          onResizeStart={() => document.body.classList.add('sidebar-resizing')}
          onResizeStop={() => document.body.classList.remove('sidebar-resizing')}
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            zIndex: 9999,
            height: '100vh',
            display: isChatbotVisible ? 'block' : 'none'
          }}
        >
          <ChatBot
            ref={chatSidebarRef}
            onHideSidebar={hideChatbot}
            userName={user?.name}
            isDarkMode={isDarkMode}
            // Pass through SQL Editor specific props if provided
            {...sqlProps}
            {...otherProps}
          />
        </ResizableBox>
      )}
    </>
  );

  // Render using portal to ensure it's always on top
  return createPortal(chatbotElement, document.body);
};

export default GlobalChatbot;